import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, CloudOff, Cloud, AlertCircle, CheckCircle, Loader, FileText, Database } from 'lucide-react';
import { Badge } from './ui/badge';
import { syncQueueManager } from '../utils/offline/syncQueue';
import type { SyncQueueItem } from '../utils/offline/indexedDB';

export default function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [queueStatus, setQueueStatus] = useState({ pending: 0, failed: 0, total: 0, syncing: 0 });
  const [pendingItems, setPendingItems] = useState<SyncQueueItem[]>([]);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Initial state
    setIsOnline(navigator.onLine);

    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for sync events
    const handleSyncEvent = (event: any) => {
      if (event.type === 'sync-start') {
        setIsSyncing(true);
      } else if (event.type === 'sync-complete' || event.type === 'sync-error') {
        setIsSyncing(false);
        updateQueueStatus();
      }
    };

    syncQueueManager.addEventListener(handleSyncEvent);

    // REMOVED: Polling-based queue status update - now event-driven only
    // const interval = setInterval(updateQueueStatus, 5000);
    updateQueueStatus();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      syncQueueManager.removeEventListener(handleSyncEvent);
      // clearInterval(interval);
    };
  }, []);

  const updateQueueStatus = async () => {
    try {
      const status = await syncQueueManager.getQueueStatus();
      setQueueStatus(status);
      
      // Also fetch the actual items if there are any
      if (status.total > 0) {
        const items = await syncQueueManager.getPendingItems();
        setPendingItems(items);
      } else {
        setPendingItems([]);
      }
    } catch (error) {
      // Silently handle errors if IndexedDB is not initialized yet
      if (error instanceof Error && error.message !== 'Database not initialized') {
        console.error('Failed to update queue status:', error);
      }
    }
  };

  // Don't show anything if online and queue is empty
  if (isOnline && queueStatus.total === 0) {
    return null;
  }

  return (
    <>
      {/* Status Indicator */}
      {!isOnline ? (
        /* Offline: Full Width Bar */
        <div
          onClick={() => setShowDetails(!showDetails)}
          className="fixed top-0 left-0 right-0 z-40 cursor-pointer bg-red-600 transition-all shadow-md"
        >
          <div className="max-w-screen-xl mx-auto px-4 py-2 flex items-center justify-between text-white">
            <div className="flex items-center space-x-2">
              <WifiOff className="h-4 w-4" />
              <span className="text-sm font-medium">Offline Mode</span>
            </div>
            <Badge className="bg-white/20 text-white border-0">
              {queueStatus.total} pending
            </Badge>
          </div>
        </div>
      ) : (
        /* Online: Floating Pill */
        <div
          onClick={() => setShowDetails(!showDetails)}
          className={`fixed top-20 right-4 z-40 cursor-pointer text-white rounded-full px-4 py-2 shadow-lg flex items-center space-x-2 transition-all ${
            queueStatus.failed > 0 ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {isSyncing ? (
            <Loader className="h-4 w-4 animate-spin" />
          ) : queueStatus.failed > 0 ? (
            <AlertCircle className="h-4 w-4" />
          ) : (
            <Cloud className="h-4 w-4" />
          )}
          <span className="text-sm font-medium">
            {isSyncing 
              ? 'Syncing...' 
              : queueStatus.failed > 0 
                ? `${queueStatus.failed} Failed` 
                : `${queueStatus.total} Pending`}
          </span>
        </div>
      )}

      {/* Detailed Status Panel */}
      {showDetails && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-50"
          onClick={() => setShowDetails(false)}
        >
          <div
            className="absolute top-10 left-4 right-4 bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`p-4 text-white ${
              isOnline
                ? queueStatus.failed > 0
                  ? 'bg-yellow-600'
                  : 'bg-blue-600'
                : 'bg-red-600'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold flex items-center space-x-2">
                  {isOnline ? <Cloud className="h-5 w-5" /> : <CloudOff className="h-5 w-5" />}
                  <span>Sync Status</span>
                </h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="p-1 rounded-full hover:bg-white/20"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-white/90">
                {isOnline
                  ? 'Connected to server'
                  : 'Working offline - changes will sync when online'}
              </p>
            </div>

            {/* Status Details */}
            <div className="p-4 space-y-3">
              {/* Connection Status */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  {isOnline ? (
                    <Wifi className="h-5 w-5 text-green-600" />
                  ) : (
                    <WifiOff className="h-5 w-5 text-red-600" />
                  )}
                  <div>
                    <p className="font-medium text-gray-900">Connection</p>
                    <p className="text-sm text-gray-600">
                      {isOnline ? 'Online' : 'Offline'}
                    </p>
                  </div>
                </div>
                <div className={`w-3 h-3 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`}></div>
              </div>

              {/* Queue Status */}
              {queueStatus.total > 0 && (
                <>
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900">Queue Status</h4>
                    
                    {queueStatus.syncing > 0 && (
                      <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                        <span className="text-sm text-blue-900">Syncing</span>
                        <Badge className="bg-blue-600 text-white">
                          {queueStatus.syncing}
                        </Badge>
                      </div>
                    )}
                    
                    {queueStatus.pending > 0 && (
                      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-900">Pending</span>
                        <Badge className="bg-gray-600 text-white">
                          {queueStatus.pending}
                        </Badge>
                      </div>
                    )}
                    
                    {queueStatus.failed > 0 && (
                      <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                        <span className="text-sm text-red-900">Failed</span>
                        <Badge className="bg-red-600 text-white">
                          {queueStatus.failed}
                        </Badge>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {isOnline && queueStatus.failed > 0 && (
                    <button
                      onClick={async () => {
                        await syncQueueManager.retryFailedItems();
                        updateQueueStatus();
                      }}
                      className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium active:bg-blue-700 transition-colors"
                    >
                      Retry Failed Items
                    </button>
                  )}

                  {/* Pending Items List */}
                  {pendingItems.length > 0 && (
                    <div className="mt-4 space-y-2 border-t pt-4">
                      <h4 className="font-medium text-gray-900 flex items-center gap-2">
                        <Database className="h-4 w-4" />
                        Pending Items
                      </h4>
                      <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
                        {pendingItems.map((item) => (
                          <div key={item.id} className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition-colors">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                                    item.type === 'CREATE' ? 'bg-green-100 text-green-800' :
                                    item.type === 'UPDATE' ? 'bg-blue-100 text-blue-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {item.type}
                                  </span>
                                  <span className="font-medium text-sm capitalize text-gray-700">{item.entity}</span>
                                </div>
                                <div className="flex items-center text-xs text-gray-500 space-x-2">
                                  <span>{new Date(item.timestamp).toLocaleTimeString()}</span>
                                  {item.retryCount > 0 && (
                                    <span className="text-orange-600">• Retry #{item.retryCount}</span>
                                  )}
                                </div>
                                {item.lastError && (
                                  <p className="text-xs text-red-600 mt-1 bg-red-50 p-1 rounded">
                                    {item.lastError}
                                  </p>
                                )}
                              </div>
                              <Badge className={`${
                                item.status === 'failed' ? 'bg-red-100 text-red-800 hover:bg-red-200' : 
                                item.status === 'syncing' ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' : 
                                'bg-gray-100 text-gray-800 hover:bg-gray-200'
                              } border-0`}>
                                {item.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Empty State */}
              {queueStatus.total === 0 && (
                <div className="text-center py-6">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="text-gray-600">All changes synced!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}